import type { ChannelConsentSummary, ConsentChannel } from './consent.types';

/** Preserve a response during history paging, but never across contact or org changes. */
export function selectConsentRead<T extends { contact: { contactId: string } | null }>(
  identity: string,
  contactId: string,
  data: T | undefined,
  last: { identity: string; data: T } | null
): { current: T | undefined; last: { identity: string; data: T } | null } {
  if (data && (!data.contact || data.contact.contactId === contactId)) {
    return { current: data, last: { identity, data } };
  }
  return { current: last?.identity === identity ? last.data : undefined, last };
}

export type ConsentViewState = 'error' | 'loading' | 'empty' | 'ready';

/**
 * Which of the tab's four states to render.
 *
 * The case worth spelling out is `error`. A read that fails with nothing cached
 * leaves no channels, and rendering that as "no consent record" would state a
 * fact about the contact that we do not have — an outage shown as unknown
 * consent, which an agent could then act on. Absent data and absent consent are
 * different things, so the failure is surfaced as itself.
 *
 * An error alongside cached channels stays `ready`: the hook keeps the last
 * good response, so a failed refetch (a history filter change, say) leaves real
 * data on screen rather than replacing it with an error.
 */
export function resolveConsentViewState({
  error,
  loading,
  channelCount,
}: {
  error?: Error | null;
  loading: boolean;
  channelCount: number;
}): ConsentViewState {
  if (channelCount > 0) return 'ready';
  if (error) return 'error';
  return loading ? 'loading' : 'empty';
}

/**
 * Whether a toggle for this channel should be sent. The card already disables
 * an ungrantable switch; this is the same rule at the mutation boundary, so a
 * stale render or a programmatic call cannot write consent the resolver will
 * discard.
 */
export function canToggleConsent(
  channels: ChannelConsentSummary[],
  channel: ConsentChannel
): boolean {
  return channels.find((c) => c.channel === channel)?.grantable ?? false;
}
